import { createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';
import {
  CountBadge,
  DashCard,
  DashGreeting,
  DashLink,
  DashRow,
  KpiStrip,
} from '@/components/ui/admin/dashboard';
import { Button } from '@/components/ui/button';
import { KeyBadge } from '@/components/ui/cifras/key-badge';
import { IconTile } from '@/components/ui/cifras/resource-card';
import { Content } from '@/components/ui/content';
import { Icon } from '@/components/ui/icon';
import { useRouter } from '@/hooks/use-router';
import { meta } from '@/lib/meta';
import { useIsAuthenticated, usePermissions } from '@/services/auth';
import { useDashboard } from '@/services/dashboard';

export const Route = createFileRoute('/_app/')({
  head: () => meta({ label: 'Dashboard' }),
  component: RouteComponent,
});

const BREADCRUMBS = {
  title: 'Dashboard',
  items: [{ label: 'Geral' }, { label: 'Dashboard', active: true }],
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function RouteComponent() {
  const { push } = useRouter();
  const { user } = useIsAuthenticated();
  const { createPermissions } = usePermissions();
  const { data } = useDashboard();

  const canUpdateSetlists = createPermissions('setlists').canUpdate;

  const latest = data?.latestSetlist;
  const pending = data
    ? [
        { label: 'Cifras sem artista', value: data.pending.noArtist, danger: true },
        {
          label: 'Cifras sem uso em repertório há 90 dias',
          value: data.pending.unused,
          danger: false,
        },
      ]
    : [];
  const pendingTotal = pending.reduce((sum, item) => sum + item.value, 0);

  return (
    <Content breadcrumbs={BREADCRUMBS}>
      <div className="flex flex-col gap-5">
        <DashGreeting date={dayjs().format('DD/MM/YYYY')} name={user?.name ?? ''} />

        <DashRow>
          <DashCard
            size="wide"
            title="Último repertório"
            subtitle={
              latest
                ? `por ${latest.user?.name ?? '—'} · ${plural(latest.items.length, 'música', 'músicas')}`
                : undefined
            }
            action={
              latest && (
                <DashLink
                  onClick={() => push('/setlists')}
                  icon={<Icon name="chevron-right" className="size-3.5" />}
                >
                  Ver todos
                </DashLink>
              )
            }
            bodyClassName="gap-5 p-6"
          >
            {latest ? (
              <>
                <h2 className="font-bold font-display text-[clamp(26px,3vw,40px)] text-white leading-[1.05] tracking-[-.03em]">
                  {latest.name}
                </h2>
                <ol className="flex flex-wrap gap-2">
                  {latest.items.map((item, index) => (
                    <li
                      key={item.id}
                      className="flex items-center gap-2 rounded-full border border-border bg-raised py-1 pr-1.5 pl-3 text-[13px] text-foreground"
                    >
                      <span className="font-bold text-accent-ink">{index + 1}</span>
                      <span className="max-w-[220px] truncate">{item.song.title}</span>
                      <KeyBadge
                        value={item.key ?? item.song.originalKey}
                        className="h-5 text-[11px]"
                      />
                    </li>
                  ))}
                </ol>
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="sand"
                    onClick={() => push(`/setlists/${latest.id}/view`)}
                  >
                    <Icon name="play" />
                    Abrir no palco
                  </Button>
                  {canUpdateSetlists && (
                    <Button
                      variant="outline"
                      onClick={() => push(`/setlists/${latest.id}`)}
                    >
                      Editar repertório
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <p className="text-[13px] text-muted-foreground">
                {data ? 'Nenhum repertório ainda.' : 'Carregando...'}
              </p>
            )}
          </DashCard>

          <DashCard
            size="narrow"
            title="Pendências"
            subtitle="O que vale revisar no acervo"
            action={pendingTotal > 0 && <CountBadge value={pendingTotal} />}
          >
            <ul className="flex flex-col">
              {pending.map((item) => (
                <li key={item.label}>
                  <button
                    type="button"
                    onClick={() => push('/songs')}
                    className="flex w-full items-center gap-3 border-border border-b px-6 py-3.5 text-left last:border-b-0 hover:bg-raised"
                  >
                    <span className="flex-1 text-[13px] text-foreground">
                      {item.label}
                    </span>
                    <span
                      className={
                        item.danger && item.value > 0
                          ? 'font-bold text-danger-soft'
                          : 'font-bold text-muted-foreground'
                      }
                    >
                      {item.value}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </DashCard>
        </DashRow>

        <KpiStrip
          items={[
            {
              label: 'Cifras',
              value: String(data?.counts.songs ?? '—'),
              note: data ? `+${data.counts.songsThisWeek} esta semana` : '',
              noteTone: 'olive',
              icon: <Icon name="music" className="size-4" />,
            },
            {
              label: 'Repertórios',
              value: String(data?.counts.setlists ?? '—'),
              note: '',
              icon: <Icon name="list-music" className="size-4" />,
            },
            {
              label: 'Artistas',
              value: String(data?.counts.artists ?? '—'),
              note: '',
              icon: <Icon name="mic-vocal" className="size-4" />,
            },
            {
              label: 'Tom mais usado',
              value: data?.topKey?.key ?? '—',
              note: data?.topKey ? plural(data.topKey.total, 'cifra', 'cifras') : '',
              noteTone: 'sand',
              icon: <Icon name="guitar" className="size-4" />,
              iconTone: 'sand',
            },
          ]}
        />

        <DashRow align="start">
          <DashCard
            size="wide"
            title="Últimos repertórios"
            action={
              <DashLink
                onClick={() => push('/setlists')}
                icon={<Icon name="chevron-right" className="size-3.5" />}
              >
                Ver todos
              </DashLink>
            }
          >
            {data?.recentSetlists.map((setlist) => (
              <button
                type="button"
                key={setlist.id}
                onClick={() => push(`/setlists/${setlist.id}/view`)}
                className="flex items-center gap-3 border-border border-b px-6 py-4 text-left last:border-b-0 hover:bg-raised"
              >
                <IconTile>
                  <Icon name="list-music" className="size-[18px]" />
                </IconTile>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate font-bold font-display text-white">
                    {setlist.name}
                  </span>
                  <span className="text-[12.5px] text-muted-foreground">
                    por {setlist.user?.name ?? '—'} ·{' '}
                    {plural(setlist.songCount, 'música', 'músicas')}
                  </span>
                </span>
                <span className="hidden gap-1.5 sm:flex">
                  {setlist.preview.map((item) => (
                    <KeyBadge
                      key={item.order}
                      value={item.key}
                      className="h-5 text-[11px]"
                    />
                  ))}
                </span>
                <Icon name="chevron-right" className="size-4 text-faint" />
              </button>
            ))}
          </DashCard>

          <DashCard
            size="narrow"
            title="Últimas cifras"
            action={
              <DashLink
                onClick={() => push('/songs')}
                icon={<Icon name="chevron-right" className="size-3.5" />}
              >
                Ver todas
              </DashLink>
            }
          >
            {data?.recentSongs.map((song) => (
              <button
                type="button"
                key={song.id}
                onClick={() => push(`/songs/${song.id}/view`)}
                className="flex items-center gap-3 border-border border-b px-6 py-4 text-left last:border-b-0 hover:bg-raised"
              >
                <IconTile>
                  <Icon name="music" className="size-[18px]" />
                </IconTile>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate font-bold font-display text-white">
                    {song.title}
                  </span>
                  <span className="truncate text-[12.5px] text-muted-foreground">
                    {song.artist?.name ?? 'Sem artista'}
                  </span>
                </span>
                <KeyBadge value={song.originalKey} />
              </button>
            ))}
          </DashCard>
        </DashRow>
      </div>
    </Content>
  );
}
